import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { TableNames } from "../../../enums/tableNames";
import { RootState } from "../../../redux/store/store";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { buildNycbsExternalFilterPayload } from "../utils/buildNycbsExternalFilterPayload";
import { NycbsExternalTableState } from "../types/nycbsExternalTableState";

export interface UseInitialDataFetchingForTableReturn {
  initialDataFetchingExecuted: boolean;
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Performs the initial data fetch when the NYCBS External table mounts.
 *
 * @param setNycbsExternalState - Updates table loading and pagination state
 * @param setTableData - Updates raw table rows
 * @param dateFromStart - Default range start date (YYYYMMDD)
 * @param dateFromEnd - Default range end date (YYYYMMDD)
 * @param pageSize - Number of rows per page
 * @param orgId - BigQuery organization id (Non-STAT or STAT)
 * @return UseInitialDataFetchingForTableReturn: Initial fetch execution flags
 */
export const useInitialDataFetchingForTable = (
  setNycbsExternalState: React.Dispatch<
    React.SetStateAction<NycbsExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number,
  orgId: string,
): UseInitialDataFetchingForTableReturn => {
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState<boolean>(false);

  const tableAttributes = getTableDataFromContext(
    TableNames.NYCBS_EXTERNAL_ORDERS,
  );

  const { filters } = useSelector((state: RootState) => state.filterValues);

  const validFilters = useMemo(() => {
    return filters.filter(
      (filter) => filter.name === TableNames.NYCBS_EXTERNAL_ORDERS,
    );
  }, [filters]);

  const setNycbsExternalStateRef = useRef(setNycbsExternalState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    setNycbsExternalStateRef.current = setNycbsExternalState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [setNycbsExternalState, setTableData, setTableDataForContext]);

  useEffect(() => {
    if (!initialDataFetchingExecuted && dateFromStart && dateFromEnd) {
      const searchedText = tableAttributes?.searchedText ?? "";
      const payload = buildNycbsExternalFilterPayload(
        validFilters,
        searchedText,
        dateFromStart,
        dateFromEnd,
        pageSize,
        orgId,
      );

      makePostCallForInitialDataFetch(
        payload,
        setNycbsExternalStateRef.current,
        setTableDataRef.current,
      ).then(() => {
        setInitialDataFetchingExecuted(true);

        setTableDataForContextRef.current(TableNames.NYCBS_EXTERNAL_ORDERS, {
          lastBatchFetched: 1,
        });
      });
    }
  }, [
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
    tableAttributes?.searchedText,
    validFilters,
    orgId,
  ]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
